import { calculateAge } from './dateUtils';
import { normalizeLevel } from './levelNormalizer';
import { decodeExcelXmlEscapes } from './textUtils';

class PTFParser {
  private content: string;
  private sections: Record<string, any[]>;

  constructor(content: string) {
    this.content = content;
    this.sections = {};
  }

  static async fromFile(file: File): Promise<PTFParser> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          resolve(new PTFParser(event.target.result as string));
        } else {
          reject(new Error('Failed to read file'));
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsText(file);
    });
  }

  parse() {
    try {
      const lines = this.content.split('\n');
      let currentMainSection: string | null = null;
      let currentSection: any = null;

      for (let line of lines) {
        line = line.trim();
        if (!line) continue;

        const sectionMatch = line.match(/^\[([\w\s-]+)(?:-(\d+))?\]$/);
        if (sectionMatch) {
          const [, sectionName, index] = sectionMatch;

          if (index) {
            const sectionBase = sectionName;
            if (!this.sections[sectionBase]) {
              this.sections[sectionBase] = [];
            }
            currentSection = {
              name: sectionName,
              records: {},
              subsections: {}
            };
            this.sections[sectionBase][parseInt(index) - 1] = currentSection;
            currentMainSection = sectionBase;
          } else {
            currentMainSection = sectionName;
            if (!this.sections[sectionName]) {
              this.sections[sectionName] = [];
            }
            if (!['meets', 'events', 'levels', 'age groups', 'gymnasts', 'gyms', 'attend'].includes(sectionName.toLowerCase())) {
              currentSection = {
                name: sectionName,
                records: {},
                subsections: {}
              };
              this.sections[sectionName][0] = currentSection;
            }
          }
          continue;
        }

        const kvMatch = line.match(/^([^=]+)=(.*)$/);
        if (kvMatch && currentSection) {
          const [, key, value] = kvMatch;
          currentSection.records[key.trim()] = value.trim();
        }
      }

      return this.sections;
    } catch (error: unknown) {
      console.error('Error parsing PTF file:', error);
      if (error instanceof Error) {
        throw new Error(`Failed to parse PTF file: ${error.message}`);
      }
      // If it's not an Error object, convert to string
      throw new Error(`Failed to parse PTF file: ${String(error)}`);
    }
  }

  // Team-related functions
  getGyms() {
    const gymKeys = Object.keys(this.sections).filter(key => key.startsWith('Gyms-'));
    return gymKeys.map(key => {
      const gym = this.sections[key][0]?.records;
      return {
        id: gym?.GymID || '',
        officialName: decodeExcelXmlEscapes(gym?.['Official Name'] || ''),
        shortName: decodeExcelXmlEscapes(gym?.['Short Name'] || ''),
        city: decodeExcelXmlEscapes(gym?.City || ''),
        state: gym?.State || '',
        email: gym?.Email || '',
        clubNumber: gym?.ClubNum || '',
        contact: decodeExcelXmlEscapes(gym?.Contact || ''),
      };
    }).filter(g => g.id);
  }

  getTotalTeams() {
    return this.getGyms().length;
  }

  getTeamsInSession(sessionNumber: number) {
    const sessionGymnasts = this.getGymnastsBySession(sessionNumber);
    const teamIds = Array.from(new Set(sessionGymnasts.filter((g): g is NonNullable<typeof g> => g !== null).map(g => g.teamId)));
    return this.getGyms().filter(gym => teamIds.includes(gym.id));
  }

  getTeamCount(sessionNumber?: string) {
    if (sessionNumber) {
      return this.getTeamsInSession(parseInt(sessionNumber)).length;
    }
    return this.getTotalTeams();
  }

  // Judge-related functions
  getJudges() {
    const judgeKeys = Object.keys(this.sections).filter(key => key.startsWith('Judges-'));
    return judgeKeys.map(key => {
      const judge = this.sections[key][0]?.records;
      return {
        id: judge?.JudgeID || '',
        firstName: decodeExcelXmlEscapes(judge?.FirstName || ''),
        lastName: decodeExcelXmlEscapes(judge?.LastName || ''),
        rating: judge?.Rating || '',
        email: judge?.Email || '',
        phone: judge?.Phone || ''
      };
    }).filter(j => j.id);
  }

  // Coach-related functions
  getCoaches() {
    const coachKeys = Object.keys(this.sections).filter(key => key.startsWith('Coaches-'));
    return coachKeys.map(key => {
      const coach = this.sections[key][0]?.records;
      return {
        id: coach?.CoachID || '',
        firstName: decodeExcelXmlEscapes(coach?.FirstName || ''),
        lastName: decodeExcelXmlEscapes(coach?.LastName || ''),
        teamId: coach?.GymID || '',
        proNumber: coach?.ProNumber || '',
        proExpire: coach?.ProExpire || '',
        safetyExpire: coach?.SafetyExpire || '',
        backExpire: coach?.BackExpire || '',
        u100: coach?.U100 === 'True',
        usagVerified: coach?.USAGVerified === 'True',
        email: coach?.Email || '',
        phone: coach?.Phone || '',
        address: decodeExcelXmlEscapes(coach?.Address || ''),
        city: decodeExcelXmlEscapes(coach?.City || ''),
        state: coach?.State || '',
        zip: coach?.Zip || ''
      };
    }).filter(c => c.id);
  }

  // Helper to find matching age group for an athlete based on DOB and level
  private findAgeGroupForAthlete(dob: string, level: string, age: number | null, ageGroups: any[]): string | undefined {
    if (!level) return undefined;

    // Parse DOB - handle both MM/DD/YYYY and YYYY-MM-DD formats
    let dobDate: Date | null = null;
    if (dob && dob.includes('/')) {
      const [month, day, year] = dob.split('/');
      dobDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    } else if (dob && dob.includes('-')) {
      dobDate = new Date(dob);
    }

    for (const ag of ageGroups) {
      // Check level matches
      if (ag.level !== level) continue;

      if (ag.useDates) {
        // Date-based matching: DOB must fall within date range
        if (!dobDate || isNaN(dobDate.getTime())) continue;

        let startDate: Date | null = null;
        let endDate: Date | null = null;

        if (ag.startDate && ag.startDate.includes('/')) {
          const [sm, sd, sy] = ag.startDate.split('/');
          startDate = new Date(parseInt(sy), parseInt(sm) - 1, parseInt(sd));
        }
        if (ag.endDate && ag.endDate.includes('/')) {
          const [em, ed, ey] = ag.endDate.split('/');
          endDate = new Date(parseInt(ey), parseInt(em) - 1, parseInt(ed));
        }

        if (!startDate || !endDate) continue;

        if (dobDate >= startDate && dobDate <= endDate) {
          return ag.name;
        }
      } else {
        // Age-based matching: athlete age must fall within minAge/maxAge range
        if (age === null || age === undefined) continue;

        const minAge = parseInt(ag.minAge) || 0;
        const maxAge = parseInt(ag.maxAge) || 99;

        if (age >= minAge && age <= maxAge) {
          return ag.name;
        }
      }
    }

    return undefined;
  }

  // Gymnast-related functions
  getGymnasts() {
    const gymnastsKeys = Object.keys(this.sections).filter(key => key.startsWith('Gymnasts-'));
    const sessions = this.getSessionsBasic();
    const competition = this.getCompetitionInfo();
    const ageGroups = this.getAgeGroups();

    // Helper to parse event scores
    const parseEventScores = (prefix: string, data: Record<string, any>) => {
      return {
        s1: data[`${prefix}S1`] || '',
        s2: data[`${prefix}S2`] || '',
        s3: data[`${prefix}S3`] || '',
        s4: data[`${prefix}S4`] || '',
        s5: data[`${prefix}S5`] || '',
        s6: data[`${prefix}S6`] || '',
        s7: data[`${prefix}S7`] || '',
        adj: data[`${prefix}Adj`] || '',
        final: parseFloat(data[`${prefix}Final`]) || 0,
        flag: data[`${prefix}Flag`] === 'True',
        bScore: data[`${prefix}BScore`] || '',
        dBonus: data[`${prefix}DBonus`] || ''
      };
    };

    return gymnastsKeys
      .map(key => {
        const gymnast = this.sections[key][0]?.records;
        if (!gymnast) return null;

        const dateOfBirth = gymnast.Birthday || '';
        const level = gymnast.Level || '';
        const session = sessions.find(s => parseInt(s.sessionNumber.toString()) === parseInt(gymnast.Session));

        // Calculate age using session date or competition date
        const age = calculateAge(dateOfBirth, session, competition);

        // Find matching age group based on DOB/age and level
        const ageGroup = this.findAgeGroupForAthlete(dateOfBirth, level, age, ageGroups);

        // Build scores object for all events (E1-E7)
        const scores: Record<string, any> = {};
        for (let i = 1; i <= 7; i++) {
          scores[`E${i}`] = parseEventScores(`E${i}`, gymnast);
        }

        // Calculate all-around score (sum of final scores)
        const aa = parseFloat(gymnast.AA) || 0;

        return {
          id: String(gymnast.Num || ''),
          number: String(gymnast.Num || ''),
          firstName: decodeExcelXmlEscapes(gymnast.First_Name || ''),
          lastName: decodeExcelXmlEscapes(gymnast.Last_Name || ''),
          teamId: String(gymnast.Gym || ''),
          level,
          dateOfBirth,
          age,
          ageGroup,
          usagId: gymnast.USAG || '',
          status: gymnast.Scratched === 'True' ? 'inactive' : 'active',
          flight: gymnast.Flight || '',
          order: gymnast.Order || '',
          squad: gymnast.Squad || '',
          session: parseInt(gymnast.Session) || 0,
          notes: gymnast.Phonetic || '',
          scratched: gymnast.Scratched === 'True',
          scores,
          aa
        };
      })
      .filter(g => g && (g.firstName || g.lastName));
  }

  parsePTFGymnast( data: any) {
    // console.log('parsePTFGymnast ', data);
    // Parse the event scores (E1-E7)
    const parseEventScores = (prefix: string, data: Record<string, any>) => {
      // console.log('parseEventScores prefix:', prefix, '', data);
      return {
        s1: data[`${prefix}S1`] || '',
        s2: data[`${prefix}S2`] || '',
        s3: data[`${prefix}S3`] || '',
        s4: data[`${prefix}S4`] || '',
        s5: data[`${prefix}S5`] || '',
        s6: data[`${prefix}S6`] || '',
        s7: data[`${prefix}S7`] || '',
        adj: data[`${prefix}Adj`] || '',
        final: parseFloat(data[`${prefix}Final`]) || 0,
        flag: data[`${prefix}Flag`] === 'True',
        order: data[`${prefix}Order`] || '',
        start: data[`${prefix}Start`] || '',
        bScore: data[`${prefix}BScore`] || '',
        hold: parseInt(data[`${prefix}Hold`]) || -1,
        cover: parseInt(data[`${prefix}Cover`]) || 0,
        selectTime: data[`${prefix}SelectTime`] || '',
        holdTime: data[`${prefix}HoldTime`] || '',
        readyTime: data[`${prefix}ReadyTime`] || '',
        startTime: data[`${prefix}StartTime`] || '',
        stopTime: data[`${prefix}StopTime`] || '',
        saveTime: data[`${prefix}SaveTime`] || '',
        status: data[`${prefix}Status`] || '',
        dBonus: data[`${prefix}DBonus`] || ''
      }
    };

    // Build scores object for all events
    const scores: any = {};
    for (let i = 1; i <= 7; i++) {
      scores[`E${i}`] = parseEventScores(`E${i}`, data);
    }

    // Normalize level using LevelCode (most specific) and Level (fallback)
    const { level: normalizedLevel } = normalizeLevel(data.LevelCode || '', data.Level || '');

    return {
      id: data.Num || '',
      meetId: data.MeetID || '',
      firstName: decodeExcelXmlEscapes(data.First_Name || ''),
      lastName: decodeExcelXmlEscapes(data.Last_Name || ''),
      level: normalizedLevel,
      levelCode: data.LevelCode || '',
      dateOfBirth: data.Birthday || '',
      usagId: data.USAG || '',
      session: parseInt(data.Session) || 0,
      teamId: data.Gym || '',
      team1: data.Team1,
      team2: data.Team2,
      team3: data.Team3,
      tSize: data.TSize,
      status: data.Scratched === 'True' ? 'inactive' : 'active',
      events: parseInt(data.Events) || 0,
      usCitizen: data.USCitizen === 'True',
      squad: data.Squad || '',
      order: data.Order || '',
      hometown: data.Hometown,
      country: data.Country,
      state: data.State,
      flight: data.Flight || '',
      altId: data.AltID,
      phonetic: data.Phonetic,
      scores,
      aaAdj: data.AAAdj,
      aa: parseFloat(data.AA) || 0,
      msoUpdate: data.MSOUpdate === 'True',
      usagVerified: data.USAGVerified === 'True',
      twoAttempts: data.TwoAttempts === 'True',
    };
  }

  getGymnastsBySession(sessionNumber: number) {
    return this.getGymnasts().filter((g): g is NonNullable<typeof g> => 
      g !== null && g.session === sessionNumber
    );
  }

  getGymnastsByTeam(teamId: string) {
    return this.getGymnasts().filter((g): g is NonNullable<typeof g> => 
      g !== null && g.teamId === String(teamId)
    );
  }

  getTeamRoster(teamId: string) {
    const team = this.getGyms().find(g => g.id === teamId);
    const gymnasts = this.getGymnastsByTeam(teamId);

    const sessionAssignments = gymnasts.reduce((acc: { [key: string]: number }, gymnast) => {
      if (gymnast && gymnast.session) {
        const sessionKey = gymnast.session.toString();
        acc[sessionKey] = (acc[sessionKey] || 0) + 1;
      }
      return acc;
    }, {});

    return {
      team,
      gymnasts,
      sessionAssignments
    };
  }

  // Helper to normalize time strings to ISO datetime format
  private normalizeTimeToISO(timeStr: string, sessionDate: string): string {
    if (!timeStr) return '';

    // If it's already an ISO datetime (contains both date and time), return as-is
    if (timeStr.match(/^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}/)) {
      // Standardize to space-separated format
      return timeStr.replace('T', ' ').split('.')[0];
    }

    // If it's a time-only string like "8:00 AM" or "08:40", convert it
    const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?/i);
    if (timeMatch) {
      let hours = parseInt(timeMatch[1]);
      const minutes = parseInt(timeMatch[2]);
      const seconds = timeMatch[3] ? parseInt(timeMatch[3]) : 0;
      const isPM = timeMatch[4]?.toUpperCase() === 'PM';

      // Convert to 24-hour format
      if (isPM && hours !== 12) hours += 12;
      if (!isPM && hours === 12) hours = 0;

      // Use session date or default to today
      const datePart = sessionDate || new Date().toISOString().split('T')[0];
      const hourStr = hours.toString().padStart(2, '0');
      const minStr = minutes.toString().padStart(2, '0');
      const secStr = seconds.toString().padStart(2, '0');

      return `${datePart} ${hourStr}:${minStr}:${secStr}`;
    }

    // If we can't parse it, return as-is
    return timeStr;
  }

  // Helper to infer gender from rotation name
  private inferGenderFromRotationName(rotationName: string): string {
    const nameLower = rotationName.toLowerCase();
    if (nameLower.includes('women') || nameLower.includes('girl') || nameLower.includes('female')) {
      return 'F';
    }
    if (nameLower.includes('men') || nameLower.includes('boy') || nameLower.includes('male')) {
      return 'M';
    }
    return '';
  }

  // Session-related functions
  getSessionsBasic() {
    const sessionKeys = Object.keys(this.sections).filter(key => key.startsWith('Sessions-'));
    const rotations = this.getRotations();
    const events = this.getEvents();
    const competition = this.getCompetitionInfo();

    return sessionKeys.map(key => {
      const session = this.sections[key][0]?.records;

      // Helper to convert MM/DD/YYYY to YYYY-MM-DD
      const normalizeDateFormat = (dateStr: string): string => {
        if (!dateStr) return '';
        // Already in YYYY-MM-DD format
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
        // Convert MM/DD/YYYY to YYYY-MM-DD
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          let [month, day, year] = parts;
          if (year.length === 2) {
            year = (parseInt(year) > 50 ? '19' : '20') + year;
          }
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        return dateStr;
      };

      // Try to extract date from various sources
      let sessionDate = normalizeDateFormat(session?.SDate || '');

      // Fallback: try competition start date (already in YYYY-MM-DD from getCompetitionInfo)
      if (!sessionDate) {
        sessionDate = competition.startDate || '';
      }

      // Infer gender: try event first, then rotation name
      const rotationId = session?.RotationID || '';
      const rotation = rotations.find(r => r.id === rotationId);
      const event = rotation ? events.find(e => e.id === rotation.eventId) : null;
      let gender = event?.gender || '';

      // Fallback: infer from rotation name (e.g., "Women Olympic Order")
      if (!gender && rotation?.name) {
        gender = this.inferGenderFromRotationName(rotation.name);
      }

      return {
        sessionNumber: parseInt(session?.Session) || 0,
        description: session?.Desc || '',
        startDate: sessionDate,
        endDate: session?.EDate || '',
        numFlights: session?.NumFlights || '',
        rotationId,
        gender,
        timeBlocks: {
          competitionStart: this.normalizeTimeToISO(session?.Competition_Start || '', sessionDate),
          competitionEnd: this.normalizeTimeToISO(session?.Competition_End || '', sessionDate),
          openStretchStart: this.normalizeTimeToISO(session?.Open_Warmup || '', sessionDate),
          warmupStart: this.normalizeTimeToISO(session?.Timed_Warmup || '', sessionDate),
          marchInStart: this.normalizeTimeToISO(session?.March_In || '', sessionDate),
          awardsEnd: this.normalizeTimeToISO(session?.Awards || session?.Awards_End || '', sessionDate),
        }
      };
    }).filter(s => s.sessionNumber);
  }

  getSessions() {
    const sessions = this.getSessionsBasic();
    return sessions.map(session => ({
      ...session,
      gymnastCount: this.getGymnastsBySession(session.sessionNumber).length
    }));
  }

  // Age Groups
  getAgeGroups() {
    const ageGroupKeys = Object.keys(this.sections).filter(key => key.startsWith('Age Groups-'));
    return ageGroupKeys.map(key => {
      const ageGroup = this.sections[key][0]?.records;
      return {
        id: ageGroup?.AgeID || '',
        name: ageGroup?.Name || '',
        minAge: ageGroup?.Low_Age || '',
        maxAge: ageGroup?.High_Age || '',
        level: ageGroup?.Level || '',
        eventId: ageGroup?.EventID || '',
        startDate: ageGroup?.Start_Date || '',
        endDate: ageGroup?.End_Date || '',
        useDates: ageGroup?.Use_Dates === 'True'
      };
    }).filter(ag => ag.id);
  }

  /**
   * Convert PTF age groups to generator ruleset format
   * Transforms birth date ranges into age brackets based on competition date
   * @param competitionDate - Optional competition date to calculate ages (defaults to competition startDate)
   * @param options - Optional configuration for generated ruleset
   */
  getAgeGroupsAsRuleset(competitionDate?: string, options?: {
    minGroupSize?: number;
    targetGroupSize?: number;
  }) {
    const ageGroups = this.getAgeGroups();
    const competition = this.getCompetitionInfo();

    // Use provided date or competition start date
    let refDate: Date;
    if (competitionDate) {
      refDate = new Date(competitionDate);
    } else if (competition.startDate) {
      refDate = new Date(competition.startDate);
    } else {
      refDate = new Date(); // Fall back to today
    }

    if (isNaN(refDate.getTime())) {
      refDate = new Date();
    }

    // Helper to parse MM/DD/YYYY date
    const parseDate = (dateStr: string): Date | null => {
      if (!dateStr) return null;
      if (dateStr.includes('/')) {
        const [m, d, y] = dateStr.split('/');
        return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
      }
      if (dateStr.includes('-')) {
        return new Date(dateStr);
      }
      return null;
    };

    // Helper to calculate age at reference date
    const calculateAgeAtDate = (birthDate: Date, atDate: Date): number => {
      let age = atDate.getFullYear() - birthDate.getFullYear();
      const monthDiff = atDate.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && atDate.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    };

    // Group age groups by level
    const levelMap = new Map<string, Array<{
      label: string;
      minAge: number;
      maxAge: number;
      originalStartDate: string;
      originalEndDate: string;
    }>>();

    for (const ag of ageGroups) {
      if (!ag.useDates) continue;

      const startDate = parseDate(ag.startDate);
      const endDate = parseDate(ag.endDate);

      if (!startDate || !endDate) continue;

      // Calculate age range at competition date
      // Someone born on startDate (earliest/oldest) will be older
      // Someone born on endDate (latest/youngest) will be younger
      const maxAge = calculateAgeAtDate(startDate, refDate);
      const minAge = calculateAgeAtDate(endDate, refDate);

      if (!levelMap.has(ag.level)) {
        levelMap.set(ag.level, []);
      }

      levelMap.get(ag.level)!.push({
        label: ag.name,
        minAge: Math.max(0, minAge), // Ensure non-negative
        maxAge: Math.max(0, maxAge),
        originalStartDate: ag.startDate,
        originalEndDate: ag.endDate
      });
    }

    // Convert to ruleset format
    const levels = Array.from(levelMap.entries()).map(([levelId, brackets]) => ({
      levelId,
      brackets: brackets.sort((a, b) => a.minAge - b.minAge), // Sort by age ascending
      minGroupSize: options?.minGroupSize ?? 3,
      targetGroupSize: options?.targetGroupSize ?? 15,
      splitPolicy: 'BY_EXACT_AGE_THEN_BIRTHDATE' as const,
      mergePolicy: 'MERGE_ADJACENT_FIRST' as const
    }));

    return {
      levels,
      source: 'PTF',
      competitionDate: refDate.toISOString().split('T')[0],
      generatedAt: new Date().toISOString()
    };
  }

  // Levels
  getLevels() {
    const levelKeys = Object.keys(this.sections).filter(key => key.startsWith('Levels-'));
    return levelKeys.map(key => {
      const level = this.sections[key][0]?.records;
      return {
        id: level?.Level || '',
        eventId: level?.EventID || '',
        taMethod: level?.TAMethod || '',
        taMethodAA: level?.TAMethodAA || '',
        color: level?.Color || '',
        levelSort: level?.LevelSort || '',
        ageDate: level?.AgeDate || '',
        startingNum: level?.StartingNum || '',
        useDBonus: level?.UseDBonus === 'True'
      };
    }).filter(l => l.id);
  }

  // Events
  getEvents() {
    const eventKeys = Object.keys(this.sections).filter(key => key.startsWith('Events-'));
    return eventKeys.map(key => {
      const event = this.sections[key][0]?.records;
      return {
        id: event?.EventID || '',
        name: event?.Name || '',
        abbrev: event?.Abbrev || '',
        gender: event?.Gender || ''
      };
    }).filter(e => e.id);
  }

  // Competition info
  getCompetitionInfo() {
    const meets = this.sections['Meets-0001']?.[0]?.records || {};
    const currentDate = new Date();
    const startDate = new Date(meets['Start_Date'] || '');
    const endDate = new Date(meets['End_Date'] || '');

    let status = 'Upcoming';
    if (currentDate > endDate) {
      status = 'Completed';
    } else if (currentDate >= startDate && currentDate <= endDate) {
      status = 'In Progress';
    }

    // Convert various date formats to YYYY-MM-DD format
    const formatDate = (dateStr: string) => {
      if (!dateStr) return '';
      try {
        // Try MM/DD/YYYY format first
        if (dateStr.includes('/')) {
          const parts = dateStr.split('/');
          if (parts.length === 3) {
            let [month, day, year] = parts;
            // Handle 2-digit year
            if (year.length === 2) {
              year = (parseInt(year) > 50 ? '19' : '20') + year;
            }
            const paddedMonth = month.padStart(2, '0');
            const paddedDay = day.padStart(2, '0');
            const formattedDate = `${year}-${paddedMonth}-${paddedDay}`;
            const date = new Date(formattedDate);
            if (!isNaN(date.getTime())) return formattedDate;
          }
        }

        // Try YYYY-MM-DD format (already correct)
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) return dateStr;
        }

        // Try parsing as generic date string
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0];
        }

        return '';
      } catch (error) {
        console.error('Error formatting date:', dateStr, error);
        return '';
      }
    };

    // Try to infer timezone from facility location (state)
    // This is a best guess - user can change it in settings
    const inferTimezone = (state: string): string => {
      const stateTimezones: Record<string, string> = {
        'AL': 'America/Chicago', 'AK': 'America/Anchorage', 'AZ': 'America/Phoenix',
        'AR': 'America/Chicago', 'CA': 'America/Los_Angeles', 'CO': 'America/Denver',
        'CT': 'America/New_York', 'DE': 'America/New_York', 'FL': 'America/New_York',
        'GA': 'America/New_York', 'HI': 'Pacific/Honolulu', 'ID': 'America/Denver',
        'IL': 'America/Chicago', 'IN': 'America/New_York', 'IA': 'America/Chicago',
        'KS': 'America/Chicago', 'KY': 'America/New_York', 'LA': 'America/Chicago',
        'ME': 'America/New_York', 'MD': 'America/New_York', 'MA': 'America/New_York',
        'MI': 'America/New_York', 'MN': 'America/Chicago', 'MS': 'America/Chicago',
        'MO': 'America/Chicago', 'MT': 'America/Denver', 'NE': 'America/Chicago',
        'NV': 'America/Los_Angeles', 'NH': 'America/New_York', 'NJ': 'America/New_York',
        'NM': 'America/Denver', 'NY': 'America/New_York', 'NC': 'America/New_York',
        'ND': 'America/Chicago', 'OH': 'America/New_York', 'OK': 'America/Chicago',
        'OR': 'America/Los_Angeles', 'PA': 'America/New_York', 'RI': 'America/New_York',
        'SC': 'America/New_York', 'SD': 'America/Chicago', 'TN': 'America/Chicago',
        'TX': 'America/Chicago', 'UT': 'America/Denver', 'VT': 'America/New_York',
        'VA': 'America/New_York', 'WA': 'America/Los_Angeles', 'WV': 'America/New_York',
        'WI': 'America/Chicago', 'WY': 'America/Denver'
      };

      // Try to extract state from facility string
      const stateMatch = meets['Facility']?.match(/\b([A-Z]{2})\b/);
      const detectedState = stateMatch ? stateMatch[1] : '';

      return stateTimezones[detectedState] || 'America/New_York';
    };

    return {
      name: decodeExcelXmlEscapes(meets['Name'] || ''),
      facility: decodeExcelXmlEscapes(meets['Facility'] || ''),
      startDate: formatDate(meets['Start_Date']),
      endDate: formatDate(meets['End_Date']),
      director: decodeExcelXmlEscapes(meets['Director'] || ''),
      sanctionId: meets['Sanction'] || '',
      id: meets['Sanction'] || '',    // using this for now as the same.
      sanctionOrg: meets['SanctionOrg'] || '',
      timezone: inferTimezone(meets['Facility'] || ''),
      details: '',
      colors: {
        primary: '',
        secondary: '',
      },
      status,
      website: '',
    };
  }

  // Check if competition already exists
  competitionExists(existingCompetitions: any[]): boolean {
    const currentCompetition = this.getCompetitionInfo();
    return existingCompetitions.some(comp =>
      comp.competition.name === currentCompetition.name &&
      comp.competition.startDate === currentCompetition.startDate &&
      comp.competition.endDate === currentCompetition.endDate
    );
  }

  // Rotations
  getRotations() {
    const rotationKeys = Object.keys(this.sections).filter(key => key.startsWith('Rotations-'));
    return rotationKeys.map(key => {
      const rotation = this.sections[key][0]?.records;
      
      // Parse squad assignments, filtering out empty ones
      const squadAssignments = Array.from({ length: 9 }, (_, i) => i + 1)
        .map(num => rotation[`Squad${num}`])
        .filter(squad => squad && squad.trim() !== '');

      return {
        id: rotation?.RotationID || '',
        eventId: rotation?.EventID || '',
        name: rotation?.Name || '',
        numSquads: parseInt(rotation?.NumSquads) || 0,
        numByes: parseInt(rotation?.NumByes) || 0,
        squadAssignments
      };
    }).filter(r => r.id);
  }

  // Statistics
  getStatistics() {
    const gymnasts = this.getGymnasts();
    const teams = this.getGyms();
    const sessions = this.getSessions();
    const ageGroups = this.getAgeGroups();
    const levels = this.getLevels();
    const events = this.getEvents();
    const coaches = this.getCoaches();
    const judges = this.getJudges();

    // Count gymnasts with scores
    const activeGymnasts = gymnasts.filter((g): g is NonNullable<typeof g> =>
      g !== null && !g.scratched
    );

    const gymnastsWithScores = activeGymnasts.filter(g => {
      if (!g.scores) return false;
      return Object.values(g.scores).some((event: any) => event?.final > 0);
    });

    const gymnastsWithAA = activeGymnasts.filter(g => g.aa && g.aa > 0);

    return {
      totalGymnasts: activeGymnasts.length,
      totalScratchedGymnasts: gymnasts.filter((g): g is NonNullable<typeof g> =>
        g !== null && g.scratched
      ).length,
      totalWithScores: gymnastsWithScores.length,
      totalWithAA: gymnastsWithAA.length,
      totalTeams: teams.length,
      totalSessions: sessions.length,
      totalAgeGroups: ageGroups.length,
      totalLevels: levels.length,
      totalEvents: events.length,
      totalJudges: judges.length,
      totalCoaches: coaches.length,
      gymnastsPerSession: sessions.map(s => ({
        session: s.sessionNumber,
        count: this.getGymnastsBySession(s.sessionNumber).length
      })),
      teamsPerSession: sessions.map(s => ({
        session: s.sessionNumber,
        count: this.getTeamsInSession(s.sessionNumber).length
      }))
    };
  }
}

export default PTFParser;
